/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { WavingHand, Business, Group, TrendingUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import type { DashboardOverviewResponse } from '../../../../types/dashboard';
import {
  calculateStudioOwnershipMetrics,
  formatMemberSince,
} from '../../../../utils/dashboardHelpers';

interface WelcomeCardProps {
  dashboardData?: DashboardOverviewResponse;
}

/**
 * Welcome card component for the dashboard.
 * Displays personalized welcome message with real user data and studio ownership summary.
 *
 * @example
 * return (
 *   <WelcomeCard dashboardData={dashboardData} />
 * );
 *
 * @returns {React.ReactElement} The welcome card component
 */
const WelcomeCard: React.FC<WelcomeCardProps> = ({ dashboardData }) => {
  const { t, i18n } = useTranslation('dashboard');
  const currentDate = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Get user data from API
  const userSummary = dashboardData?.data?.user_summary;
  const studios = dashboardData?.data?.studios || [];

  // Calculate studio ownership metrics
  const ownershipMetrics = calculateStudioOwnershipMetrics(studios);

  // Get user's name from API data or fallback
  const userName = userSummary?.name || 'Studio Owner';
  const memberSince = userSummary?.member_since;

  // Generate personalized greeting
  const getGreeting = () => {
    if (ownershipMetrics.ownedStudios === 0) {
      return t('welcome.greeting.noStudios');
    }
    if (ownershipMetrics.ownedStudios === 1) {
      return t('welcome.greeting.oneStudio', {
        studioName: ownershipMetrics.ownedStudiosList[0]?.name || 'your studio',
      });
    }
    return t('welcome.greeting.multipleStudios', {
      count: ownershipMetrics.ownedStudios,
    });
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WavingHand sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" component="h2">
            {t('welcome.back', { name: userName })}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {currentDate}
          {memberSince &&
            ` • ${t('welcome.memberSince', { date: formatMemberSince(memberSince) })}`}
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          {getGreeting()}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {ownershipMetrics.ownedStudios > 0 && (
            <Chip
              icon={<Business />}
              label={
                ownershipMetrics.ownedStudios > 1
                  ? t('welcome.chips.studios_plural', {
                      count: ownershipMetrics.ownedStudios,
                    })
                  : t('welcome.chips.studios', {
                      count: ownershipMetrics.ownedStudios,
                    })
              }
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          {studios.length > ownershipMetrics.ownedStudios && (
            <Chip
              icon={<Group />}
              label={
                ownershipMetrics.memberStudios > 1
                  ? t('welcome.chips.memberships', {
                      count: ownershipMetrics.memberStudios,
                    })
                  : t('welcome.chips.membership', {
                      count: ownershipMetrics.memberStudios,
                    })
              }
              color="secondary"
              variant="outlined"
              size="small"
            />
          )}
          {userSummary?.profile_completion && (
            <Chip
              icon={<TrendingUp />}
              label={t('welcome.chips.profileComplete', {
                percentage: userSummary.profile_completion,
              })}
              color={
                userSummary.profile_completion >= 80 ? 'success' : 'warning'
              }
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default WelcomeCard;
